import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/works";

const FORM_ADDR = "https://readdy.ai/api/form/dah85srp14a1h3mr6f0g";
const MAX_LEN = 500;

type Status = "idle" | "submitting" | "success" | "error";

export default function SubmitForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [length, setLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    formData.delete("company_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string" && value.trim() !== "") {
          body.append(key, value);
        }
      });

      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setLength(0);
      } else {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请检查网络后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
        <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-2xl">
          <i className="ri-checkbox-circle-line" />
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">投稿已提交</h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
          感谢你的投稿。稿件会先经过内容审核，通常三个工作日内完成，通过后就会出现在参赛作品集中。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 inline-flex items-center gap-1.5 bg-background-100 hover:bg-background-200 text-foreground-800 text-sm font-medium px-4 py-2.5 rounded-md whitespace-nowrap transition-colors cursor-pointer"
        >
          继续投下一篇
        </button>
      </div>
    );
  }

  return (
    <form
      id="contest-submit-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
      noValidate={false}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="nickname" className="block text-sm font-medium text-foreground-800 mb-2">
            你的昵称
          </label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            required
            placeholder="例如：夜航船"
            className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-2">
            联系邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
        <div>
          <label htmlFor="workTitle" className="block text-sm font-medium text-foreground-800 mb-2">
            作品标题
          </label>
          <input
            id="workTitle"
            name="work_title"
            type="text"
            required
            placeholder="给这篇爆料起个名字"
            className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
        <div>
          <label htmlFor="workCategory" className="block text-sm font-medium text-foreground-800 mb-2">
            作品方向
          </label>
          <select
            id="workCategory"
            name="work_category"
            required
            defaultValue=""
            className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors cursor-pointer"
          >
            <option value="" disabled>
              请选择作品方向
            </option>
            {categories.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between gap-4 mb-2">
          <label htmlFor="workContent" className="block text-sm font-medium text-foreground-800">
            作品内容简介
          </label>
          <span className={`text-xs ${length > MAX_LEN ? "text-primary-600" : "text-foreground-500"}`}>
            {length} / {MAX_LEN}
          </span>
        </div>
        <textarea
          id="workContent"
          name="work_content"
          required
          maxLength={MAX_LEN}
          rows={6}
          onChange={(e) => setLength(e.target.value.length)}
          placeholder="简单描述这篇爆料的核心内容，说明时间、场景与关键细节，方便我们快速判断选题方向。"
          className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none leading-relaxed"
        />
      </div>

      <div className="form-guard-field" aria-hidden="true">
        <label htmlFor="company_alt">Company</label>
        <input
          id="company_alt"
          name="company_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "error" && (
        <p role="alert" className="mt-4 text-sm text-primary-700 bg-primary-100 border border-primary-200 rounded-md px-4 py-3">
          {formError}
        </p>
      )}

      <div className="mt-6 flex flex-col sm:flex-row items-center gap-4 justify-between">
        <p className="text-xs text-foreground-500 leading-relaxed">
          提交即表示同意作品在爆料大赛作品集中公开展示，请勿包含与内容无关的隐私信息。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-medium px-6 py-3 rounded-md whitespace-nowrap transition-colors cursor-pointer"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
          </span>
          {status === "submitting" ? "正在提交…" : "提交参赛作品"}
        </button>
      </div>
    </form>
  );
}